import { Injectable, signal, computed, WritableSignal, Signal } from '@angular/core';
import {
  MediaPlayerStoreType,
  MediaTranscodingStatus,
  MediaPlayerFile,
} from '../interfaces/media-player-store.interface';
import { AnnotationPrivateCondition } from '../interfaces/annotation.interface';
import { ActiveSubsectionState, SimpleFilePage, MalwareScanningStatus } from '../interfaces';
import { SafeStyle } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';

export class MediaPlayerStore {
  private createInitialState(): MediaPlayerStoreType {
    return {
      // Component input properties
      instanceId: '',
      projectId: null,
      sectionId: null,
      subsectionId: null,
      typeId: null,
      item: null,
      projectParticipants: [],
      showDownloadButtonIfNotPreviewAble: false,
      portfolioPreview: false,
      respondToTaskQueue: false,
      enableResponsive: false,
      enableAnnotation: false,
      parentElementContainer: null,
      activePage: null,
      commentOnly: false,
      hideAnnotationToolbar: false,
      oldItem: false,
      // Common states
      mediaType: null,
      fileTranscodingStatus: 'idle',
      isLoading: false,
      isBusy: false,
      isFileNotSupported: false,
      useFallbackDisplay: false,
      isFullscreen: false,
      isShareLinkPreview: false,
      isReleasePreview: false,
      isAssetListVisible: false,
      isAssetInfoVisible: false,
      audioGap: 52,
      isPlayerReady: false,
      isVideoPlayerLoaded: false,
      isVideoPluginReady: false,
      isAudioPluginReady: false,
      player: null,
      hasEditPermission: false,
      userInfo: null,
      privateCondition: 'ALL',
      annotations: [],
      activeAnnotationId: null,
      isImagePluginReady: false,
      isCommentPluginReady: false,
      annotationsLoaded: false,
      subsectionState: null,
      isError: false,
      annotationPlugin: null,
      videoOptions: null,
      videoSourceCheckInterval: null,
      videoTypeCheckInterval: null,
      imagePristine: false,
      imgSrc: null,
      videoViewerAction: null,
      audioViewerAction: null,
      videoPreloadMethod: null,
      isVideoSourceSet: false,
    };
  }

  private readonly _state: WritableSignal<MediaPlayerStoreType> = signal<MediaPlayerStoreType>(
    this.createInitialState()
  );

  readonly state: Signal<MediaPlayerStoreType> = this._state.asReadonly();

  // Component input properties
  readonly instanceIdSignal: Signal<string> = computed(() => this._state().instanceId);
  readonly projectIdSignal: Signal<string | null> = computed(() => this._state().projectId);
  readonly sectionIdSignal: Signal<string | null> = computed(() => this._state().sectionId);
  readonly subsectionIdSignal: Signal<string | null> = computed(() => this._state().subsectionId);
  readonly typeIdSignal: Signal<string | null> = computed(() => this._state().typeId);
  readonly itemSignal: Signal<MediaPlayerFile | null> = computed(() => this._state().item);
  readonly projectParticipantsSignal: Signal<any[]> = computed(() => this._state().projectParticipants);
  readonly showDownloadButtonIfNotPreviewAbleSignal: Signal<boolean> = computed(
    () => this._state().showDownloadButtonIfNotPreviewAble
  );
  readonly portfolioPreviewSignal: Signal<boolean> = computed(() => this._state().portfolioPreview);
  readonly respondToTaskQueueSignal: Signal<boolean> = computed(() => this._state().respondToTaskQueue);
  readonly enableResponsiveSignal: Signal<boolean> = computed(() => this._state().enableResponsive);
  readonly enableAnnotationSignal: Signal<boolean> = computed(() => this._state().enableAnnotation);
  readonly parentElementContainerSignal: Signal<string | null> = computed(() => this._state().parentElementContainer);
  readonly activePageSignal: Signal<SimpleFilePage | null> = computed(() => this._state().activePage);
  readonly commentOnlySignal: Signal<boolean> = computed(() => this._state().commentOnly);
  readonly hideAnnotationToolbarSignal: Signal<boolean> = computed(() => this._state().hideAnnotationToolbar);
  readonly oldItemSignal: Signal<boolean> = computed(() => this._state().oldItem);

  // File and media state
  readonly mediaTypeSignal: Signal<string | null> = computed(() => this._state().mediaType);
  readonly fileTranscodingStatusSignal: Signal<MediaTranscodingStatus> = computed(
    () => this._state().fileTranscodingStatus
  );
  readonly isLoadingSignal: Signal<boolean> = computed(() => this._state().isLoading);
  readonly isBusySignal: Signal<boolean> = computed(() => this._state().isBusy);
  readonly isFileNotSupportedSignal: Signal<boolean> = computed(() => this._state().isFileNotSupported);
  readonly useFallbackDisplaySignal: Signal<boolean> = computed(() => this._state().useFallbackDisplay);

  // UI state
  readonly isFullscreenSignal: Signal<boolean> = computed(() => this._state().isFullscreen);
  readonly isShareLinkPreviewSignal: Signal<boolean> = computed(() => this._state().isShareLinkPreview);
  readonly isReleasePreviewSignal: Signal<boolean> = computed(() => this._state().isReleasePreview);
  readonly isAssetListVisibleSignal: Signal<boolean> = computed(() => this._state().isAssetListVisible);
  readonly isAssetInfoVisibleSignal: Signal<boolean> = computed(() => this._state().isAssetInfoVisible);
  readonly audioGapSignal: Signal<number> = computed(() => this._state().audioGap);
  readonly isPlayerReadySignal: Signal<boolean> = computed(() => this._state().isPlayerReady);
  readonly isVideoPlayerLoadedSignal: Signal<boolean> = computed(() => this._state().isVideoPlayerLoaded);
  readonly isVideoPluginReadySignal: Signal<boolean> = computed(() => this._state().isVideoPluginReady);
  readonly isAudioPluginReadySignal: Signal<boolean> = computed(() => this._state().isAudioPluginReady);

  readonly isMediaPlayerLoadedSignal: Signal<boolean> = computed(() => {
    const mediaType: string | null = this._state().mediaType;
    switch (mediaType) {
      case 'video':
        return this._state().isVideoPluginReady;
      case 'audio':
        return this._state().isAudioPluginReady;
      default:
        return true;
    }
  });

  readonly playerSignal: Signal<any> = computed(() => this._state().player);
  readonly hasEditPermissionSignal: Signal<boolean> = computed(() => this._state().hasEditPermission);
  readonly userInfoSignal: Signal<Record<string, any> | null> = computed(() => this._state().userInfo);

  // Annotation state
  readonly privateConditionSignal: Signal<AnnotationPrivateCondition> = computed(() => this._state().privateCondition);
  readonly annotationsSignal: Signal<any[]> = computed(() => this._state().annotations);
  readonly isImagePluginReadySignal: Signal<boolean> = computed(() => this._state().isImagePluginReady);
  readonly isCommentPluginReadySignal: Signal<boolean> = computed(() => this._state().isCommentPluginReady);
  readonly annotationsLoadedSignal: Signal<boolean> = computed(() => this._state().annotationsLoaded);
  readonly activeAnnotationIdSignal: Signal<string | null> = computed(() => this._state().activeAnnotationId);

  // Player state
  readonly imagePristineSignal: Signal<boolean> = computed(() => this._state().imagePristine);
  readonly imgSrcSignal: Signal<SafeStyle | null> = computed(() => this._state().imgSrc);

  // Subsection context
  readonly subsectionStateSignal: Signal<ActiveSubsectionState | null> = computed(() => this._state().subsectionState);

  // Error state
  readonly isErrorSignal: Signal<boolean> = computed(() => this._state().isError);

  // Plugin and component state
  readonly annotationPluginSignal: Signal<any> = computed(() => this._state().annotationPlugin);
  readonly videoOptionsSignal: Signal<Record<string, any> | null> = computed(() => this._state().videoOptions);
  readonly videoSourceCheckIntervalSignal: Signal<ReturnType<typeof setTimeout> | null> = computed(
    () => this._state().videoSourceCheckInterval
  );
  readonly videoTypeCheckIntervalSignal: Signal<ReturnType<typeof setTimeout> | null> = computed(
    () => this._state().videoTypeCheckInterval
  );

  // Action triggers for component communication
  readonly videoViewerActionSignal: Signal<string | null> = computed(() => this._state().videoViewerAction);
  readonly audioViewerActionSignal: Signal<string | null> = computed(() => this._state().audioViewerAction);

  // Video player configuration
  readonly videoPreloadMethodSignal: Signal<string | null> = computed(() => this._state().videoPreloadMethod);
  readonly isVideoSourceSetSignal: Signal<boolean> = computed(() => this._state().isVideoSourceSet);

  // Computed media type signals
  readonly isImageMedia: Signal<boolean> = computed(() => this._state().mediaType === 'image');
  readonly isVideoMedia: Signal<boolean> = computed(() => this._state().mediaType === 'video');
  readonly isAudioMedia: Signal<boolean> = computed(() => this._state().mediaType === 'audio');
  readonly isPdfMedia: Signal<boolean> = computed(() => this._state().mediaType === 'pdf');

  readonly hasErrorSignal: Signal<boolean> = computed(() => this.state().isError);

  readonly isLoadingOrBeingTranscoded: Signal<boolean> = computed(() => {
    return this.state().isLoading || this.state().fileTranscodingStatus === 'in-progress';
  });

  readonly isNotCleanOrBeingTranscoded: Signal<boolean> = computed(() => {
    const malwareState: MalwareScanningStatus = this.state().item?.malwareScanningStatus ?? MalwareScanningStatus.Clean;
    return this.isLoadingOrBeingTranscoded() || malwareState !== MalwareScanningStatus.Clean;
  });

  readonly itemIdSignal: Signal<string | null> = computed(() => {
    const state: MediaPlayerStoreType = this._state();
    const { item, activePage } = state;
    if (!item) {
      return null;
    }
    return item.hasPage && !!activePage ? activePage.id : item.id;
  });

  readonly typeIdComputedSignal: Signal<string | null> = computed(() => {
    const state: MediaPlayerStoreType = this._state();
    const { item, activePage } = state;
    return item?.hasPage && !!activePage ? item.id : state.typeId;
  });

  constructor(instanceId: string = uuidv4()) {
    this._state.update((state: MediaPlayerStoreType) => ({ ...state, instanceId }));
  }

  public resetState(): void {
    this._state.set(this.createInitialState());
  }

  public dispose(): void {
    this.resetState();
  }

  public updateStore(updates: Partial<MediaPlayerStoreType>): void {
    this._state.update((state: MediaPlayerStoreType) => ({ ...state, ...updates }));
  }
}

/**
 * Singleton service that acts as a factory for media player state stores.
 * Manages shared state across components (media-player, comments, leave-comment, versions-comparer)
 * using a Map of instanceId → stateStore.
 */
@Injectable({
  providedIn: 'root',
})
export class MediaPlayerStoreService {
  private readonly stores: Map<string, MediaPlayerStore> = new Map();

  constructor() {
    // (window as any).mpStoreService = this;
  }

  public createStore(instanceId: string): MediaPlayerStore {
    if (!instanceId) {
      throw new Error('instanceId is required to create a media player state store');
    }
    if (this.stores.has(instanceId)) {
      return this.stores.get(instanceId);
    }
    const store: MediaPlayerStore = new MediaPlayerStore(instanceId);
    this.stores.set(instanceId, store);
    return store;
  }

  public getStore(instanceId: string): MediaPlayerStore | null {
    if (!instanceId) {
      throw new Error('instanceId is required to get a media player state store');
    }

    if (!this.stores.has(instanceId)) {
      return null;
    }

    return this.stores.get(instanceId);
  }

  public hasStore(instanceId: string): boolean {
    return this.stores.has(instanceId);
  }

  public disposeStore(instanceId: string): void {
    const store: MediaPlayerStore | null = this.stores.get(instanceId);
    if (store) {
      store.dispose();
      this.stores.delete(instanceId);
    }
  }

  public disposeAllStores(): void {
    this.stores.forEach((store) => store.dispose());
    this.stores.clear();
  }

  public getStoreCount(): number {
    return this.stores.size;
  }
}
